import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import api from '../services/api';
import { 
  Calendar, 
  FileText, 
  Plus, 
  Search, 
  Filter, 
  Download,
  Edit2,
  Trash2,
  AlertCircle,
  CheckCircle,
  Truck,
  Wrench,
  RefreshCw
} from 'lucide-react';
import * as XLSX from 'xlsx';

const MaintenanceSchedulePage = () => {
  const [records, setRecords] = useState([]);
  const [trucks, setTrucks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedTruck, setSelectedTruck] = useState('');
  const [selectedType, setSelectedType] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);

  const months2023 = ['Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
  const months2024 = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
  
  const getMonthsForYear = (year) => {
    return year === 2023 ? months2023 : months2024;
  };

  // Generar años disponibles desde 2023 hasta el año actual + 1
  const currentYear = new Date().getFullYear();
  const availableYears = Array.from(
    { length: currentYear - 2023 + 2 }, 
    (_, i) => 2023 + i
  );

  useEffect(() => {
    fetchRecords();
    fetchTrucks();
  }, [selectedYear, selectedTruck, selectedType]);

  const fetchRecords = async () => {
    try {
      setLoading(true);
      let url = `/maintenance-schedule?year=${selectedYear}`;
      
      if (selectedTruck) url += `&truck_id=${selectedTruck}`;
      if (selectedType) url += `&tipo_mantenimiento=${selectedType}`;
      
      const response = await api.get(url);
      const data = response.data;
      // Asegurarse de que records sea siempre un array
      setRecords(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error al cargar registros:', error);
      setRecords([]); // Establecer array vacío en caso de error
    } finally {
      setLoading(false);
    }
  };

  const fetchTrucks = async () => {
    try {
      const response = await api.get('/trucks?limit=1000');
      const data = response.data;
      // Asegurarse de que trucks sea siempre un array
      setTrucks(Array.isArray(data.trucks) ? data.trucks : Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error al cargar unidades:', error);
      setTrucks([]); // Establecer array vacío en caso de error
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Está seguro de eliminar este registro?')) return;
    
    try {
      await api.delete(`/maintenance-schedule/${id}`);
      fetchRecords();
    } catch (error) {
      console.error('Error al eliminar:', error);
      alert('Error al eliminar el registro');
    }
  };

  const handleEdit = (record) => {
    setEditingRecord(record);
    setShowModal(true);
  };

  const handleSync = async () => {
    if (!window.confirm('¿Desea sincronizar los mantenimientos desde la tabla de mantenimientos?\n\nEsto importará todos los mantenimientos registrados al calendario.')) return;
    
    setLoading(true);
    try {
      const response = await api.post('/maintenance-sync/sync-from-mantenimientos');
      const data = response.data;
      
      if (data.success) {
        alert(`✅ Sincronización exitosa!\n\n` +
              `Total mantenimientos: ${data.stats.total}\n` +
              `Sincronizados: ${data.stats.sincronizados}\n` +
              `Errores: ${data.stats.errores}`);
        fetchRecords();
      }
    } catch (error) {
      console.error('Error en sincronización:', error);
      alert('❌ Error al sincronizar mantenimientos');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditingRecord(null);
    setShowModal(true);
  };

  const exportToExcel = () => {
    const monthsToShow = getMonthsForYear(selectedYear);
    
    const exportData = records.map(record => {
      const row = {
        'UNIDAD': record.numero_economico,
        'TIPO': record.tipo_unidad,
        'Fecha Mantto': new Date(record.fecha_mantenimiento).toLocaleDateString('es-MX'),
        'Km Mtto': record.km_mantenimiento,
        'Km Actual': record.km_actual,
        'Prox. Camb.': record.prox_cambio,
        'Tipo Mantenimiento': record.tipo_mantenimiento,
        'Observaciones': record.observaciones || ''
      };

      // Agregar columnas de cada mes con sus 4 semanas
      monthsToShow.forEach(month => {
        for (let week = 1; week <= 4; week++) {
          const key = `${month}-Sem${week}`;
          row[key] = record.meses?.[month]?.[week] ? 'X' : '';
        }
      });

      return row;
    });

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, `Mantenimientos ${selectedYear}`);
    
    XLSX.writeFile(wb, `Programa_Mantenimientos_${selectedYear}.xlsx`);
  };

  const filteredRecords = records.filter(record => {
    const searchLower = searchTerm.toLowerCase();
    return (
      record.numero_economico?.toLowerCase().includes(searchLower) ||
      record.tipo_unidad?.toLowerCase().includes(searchLower) ||
      record.marca?.toLowerCase().includes(searchLower) ||
      record.modelo?.toLowerCase().includes(searchLower)
    );
  });

  const monthsToShow = getMonthsForYear(selectedYear);

  // Agrupar registros por unidad
  const groupedByTruck = filteredRecords.reduce((acc, record) => {
    const key = record.numero_economico;
    if (!acc[key]) {
      acc[key] = {
        truck: {
          numero_economico: record.numero_economico,
          tipo_unidad: record.tipo_unidad,
          km_actual: record.km_actual,
          id: record.truck_id
        },
        programados: {},
        realizados: {}
      };
      
      // Inicializar meses
      monthsToShow.forEach(month => {
        acc[key].programados[month] = { '1': [], '2': [], '3': [], '4': [] };
        acc[key].realizados[month] = { '1': [], '2': [], '3': [], '4': [] };
      });
    }
    
    // Separar entre programados y realizados según el tipo
    const targetObj = record.tipo_mantenimiento === 'Preventivo' && record.km_actual < record.km_mantenimiento 
      ? acc[key].programados 
      : acc[key].realizados;
    
    const meses = record.meses || {};
    Object.keys(meses).forEach(month => {
      if (targetObj[month]) {
        Object.keys(meses[month]).forEach(week => {
          if (meses[month][week] && targetObj[month][week]) {
            targetObj[month][week].push({
              tipo: record.tipo_mantenimiento,
              fecha: record.fecha_mantenimiento,
              id: record.id
            });
          }
        });
      }
    });
    
    return acc;
  }, {});

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 p-2" style={{ zoom: '0.85' }}>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full mx-auto"
      >
        {/* Header */}
        <div className="bg-white rounded-3xl shadow-xl p-4 mb-4 border border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="bg-gradient-to-br from-blue-500 to-indigo-600 p-3 rounded-2xl shadow-lg">
                <Calendar className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-800">
                  Programa y Registro de Mantenimientos
                </h1>
                <p className="text-sm text-gray-600 mt-1">
                  FO.VIII.02 - Control de mantenimientos preventivos y correctivos
                </p>
              </div>
            </div>
            
            <div className="flex gap-3">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={handleSync}
                className="flex items-center gap-2 bg-purple-600 hover:bg-purple-700 text-white px-6 py-3 rounded-xl shadow-lg transition-all"
                disabled={loading}
              >
                <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
                Sincronizar
              </motion.button>
              
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={exportToExcel}
                className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-xl shadow-lg transition-all"
              >
                <Download className="w-5 h-5" />
                Exportar Excel
              </motion.button>
              
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={handleAdd}
                className="flex items-center gap-2 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white px-6 py-3 rounded-xl shadow-lg transition-all"
              >
                <Plus className="w-5 h-5" />
                Nuevo Registro
              </motion.button>
            </div>
          </div>

          {/* Filtros */}
          <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Buscar unidad..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <select
              value={selectedYear}
              onChange={(e) => setSelectedYear(Number(e.target.value))}
              className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              {availableYears.map(year => (
                <option key={year} value={year}>Año: {year}</option>
              ))}
            </select>

            <select
              value={selectedTruck}
              onChange={(e) => setSelectedTruck(e.target.value)}
              className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">Todas las unidades</option>
              {trucks.map(truck => (
                <option key={truck.id} value={truck.id}>
                  {truck.numero_economico} - {truck.tipo_unidad}
                </option>
              ))}
            </select>

            <select
              value={selectedType}
              onChange={(e) => setSelectedType(e.target.value)}
              className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">Todos los tipos</option>
              <option value="Preventivo">Preventivo</option>
              <option value="Correctivo">Correctivo</option>
            </select>

            <div className="flex items-center gap-2 bg-blue-50 px-4 py-2 rounded-lg">
              <FileText className="w-5 h-5 text-blue-600" />
              <span className="text-sm font-medium text-blue-900">
                {Object.keys(groupedByTruck).length} unidades
              </span>
            </div>
          </div>
        </div>

        {/* Tabla */}
        <div className="bg-white rounded-3xl shadow-xl p-3 overflow-x-auto border border-gray-200">
          {loading ? (
            <div className="flex justify-center items-center py-20">
              <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600"></div>
            </div>
          ) : filteredRecords.length === 0 ? (
            <div className="text-center py-20">
              <Calendar className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500 text-lg">No hay registros para mostrar</p>
              <p className="text-gray-400">Agrega un nuevo registro para comenzar</p>
            </div>
          ) : (
            <table className="w-full text-xs">
              <thead>
                <tr className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white">
                  <th className="px-1.5 py-2 text-center font-semibold sticky left-0 bg-blue-600 z-10">Unidad</th>
                  <th className="px-1.5 py-2 text-center font-semibold">Tipo</th>
                  <th colSpan="4" className="px-1.5 py-2 text-center font-semibold">Información</th>
                  
                  {/* Meses con semanas */}
                  {monthsToShow.map(month => (
                    <React.Fragment key={month}>
                      <th colSpan="4" className="px-1 py-2 text-center font-semibold border-l-2 border-white">
                        {month}
                      </th>
                    </React.Fragment>
                  ))}
                  
                  <th className="px-3 py-4 text-center font-semibold border-l-2 border-white">Acciones</th>
                </tr>
                <tr className="bg-blue-500 text-white text-xs">
                  <th colSpan="6"></th>
                  {monthsToShow.map(month => (
                    <React.Fragment key={`${month}-weeks`}>
                      <th className="px-0.5 py-2 text-center border-l border-white text-xs">1</th>
                      <th className="px-0.5 py-2 text-center text-xs">2</th>
                      <th className="px-0.5 py-2 text-center text-xs">3</th>
                      <th className="px-0.5 py-2 text-center text-xs">4</th>
                    </React.Fragment>
                  ))}
                  <th className="px-1.5 py-2 text-center font-semibold text-xs">Prog/Real</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(groupedByTruck).map(([truckNumber, data], groupIndex) => (
                  <React.Fragment key={truckNumber}>
                    {/* Fila 1: Mantenimientos PROGRAMADOS */}
                    <motion.tr
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: groupIndex * 0.05 }}
                      className="border-b border-gray-300 bg-blue-50"
                    >
                      <td rowSpan="2" className="px-1.5 py-2 font-bold text-blue-900 sticky left-0 bg-blue-100 border-r-2 border-blue-300 align-middle text-center">
                        {data.truck.numero_economico}
                      </td>
                      <td className="px-1.5 py-1 text-xs text-gray-600">
                        <div className="flex items-center gap-1">
                          <Calendar className="w-3 h-3 text-purple-600" />
                          <span className="font-semibold">Programado</span>
                        </div>
                      </td>
                      <td colSpan="4" className="px-1.5 py-1 text-xs text-gray-500 italic">
                        {data.truck.tipo_unidad}
                      </td>
                      
                      {/* Calendario programados */}
                      {monthsToShow.map(month => (
                        <React.Fragment key={`prog-${truckNumber}-${month}`}>
                          {[1, 2, 3, 4].map(week => {
                            const items = data.programados[month]?.[week] || [];
                            return (
                              <td key={`prog-${truckNumber}-${month}-${week}`} className="px-0.5 py-1 text-center border-l border-gray-200 bg-purple-50">
                                {items.length > 0 ? (
                                  <div className="flex flex-col gap-0.5">
                                    {items.map((item, idx) => (
                                      <Calendar 
                                        key={idx} 
                                        className={`w-3 h-3 mx-auto ${
                                          item.tipo === 'Preventivo' ? 'text-blue-600' : 'text-orange-600'
                                        }`} 
                                      />
                                    ))}
                                  </div>
                                ) : (
                                  <div className="w-3 h-3 mx-auto"></div>
                                )}
                              </td>
                            );
                          })}
                        </React.Fragment>
                      ))}
                      
                      <td className="px-1 py-1 text-center border-l border-gray-200">
                        <motion.button
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.9 }}
                          onClick={() => {
                            // Crear/editar mantenimiento programado
                            const programado = filteredRecords.find(
                              r => r.numero_economico === truckNumber && 
                                   r.tipo_mantenimiento === 'Preventivo' &&
                                   r.km_actual < r.km_mantenimiento
                            );
                            
                            if (programado) {
                              handleEdit(programado);
                            } else {
                              // Crear nuevo programado
                              setEditingRecord({
                                truck_id: data.truck.id,
                                numero_economico: truckNumber,
                                tipo_mantenimiento: 'Preventivo',
                                km_actual: data.truck.km_actual,
                                year: selectedYear,
                                isProgrammed: true
                              });
                              setShowModal(true);
                            }
                          }}
                          className="p-1 text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
                          title="Editar/Crear Programado"
                        >
                          <Calendar className="w-3 h-3" />
                        </motion.button>
                      </td>
                    </motion.tr>

                    {/* Fila 2: Mantenimientos REALIZADOS */}
                    <motion.tr
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: groupIndex * 0.05 + 0.025 }}
                      className="border-b-2 border-gray-400 hover:bg-green-50 transition-colors"
                    >
                      <td className="px-1.5 py-1 text-xs text-gray-600">
                        <div className="flex items-center gap-1">
                          <CheckCircle className="w-3 h-3 text-green-600" />
                          <span className="font-semibold">Realizado</span>
                        </div>
                      </td>
                      <td colSpan="4" className="px-1.5 py-1 text-xs text-gray-600">
                        Km: {data.truck.km_actual?.toLocaleString()}
                      </td>
                      
                      {/* Calendario realizados */}
                      {monthsToShow.map(month => (
                        <React.Fragment key={`real-${truckNumber}-${month}`}>
                          {[1, 2, 3, 4].map(week => {
                            const items = data.realizados[month]?.[week] || [];
                            return (
                              <td key={`real-${truckNumber}-${month}-${week}`} className="px-0.5 py-1 text-center border-l border-gray-200">
                                {items.length > 0 ? (
                                  <div className="flex flex-col gap-0.5">
                                    {items.map((item, idx) => (
                                      item.tipo === 'Preventivo' ? (
                                        <CheckCircle key={idx} className="w-3 h-3 text-green-600 mx-auto" />
                                      ) : (
                                        <Wrench key={idx} className="w-3 h-3 text-red-600 mx-auto" />
                                      )
                                    ))}
                                  </div>
                                ) : (
                                  <div className="w-3 h-3 mx-auto"></div>
                                )}
                              </td>
                            );
                          })}
                        </React.Fragment>
                      ))}
                      
                      <td className="px-1 py-1 text-center border-l border-gray-200">
                        <motion.button
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.9 }}
                          onClick={() => {
                            // Crear/editar mantenimiento realizado
                            const realizado = filteredRecords.find(
                              r => r.numero_economico === truckNumber && 
                                   (r.tipo_mantenimiento === 'Correctivo' || 
                                    (r.tipo_mantenimiento === 'Preventivo' && r.km_actual >= r.km_mantenimiento))
                            );
                            
                            if (realizado) {
                              handleEdit(realizado);
                            } else {
                              // Crear nuevo realizado
                              setEditingRecord({
                                truck_id: data.truck.id,
                                numero_economico: truckNumber,
                                tipo_mantenimiento: 'Preventivo',
                                km_actual: data.truck.km_actual,
                                year: selectedYear,
                                isProgrammed: false
                              });
                              setShowModal(true);
                            }
                          }}
                          className="p-1 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                          title="Editar/Crear Realizado"
                        >
                          <CheckCircle className="w-3 h-3" />
                        </motion.button>
                      </td>
                    </motion.tr>
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </motion.div>

      {/* Modal - Próximamente */}
      {showModal && (
        <MaintenanceModal
          record={editingRecord}
          trucks={trucks}
          year={selectedYear}
          onClose={() => {
            setShowModal(false);
            setEditingRecord(null);
          }}
          onSave={() => {
            fetchRecords();
            setShowModal(false);
            setEditingRecord(null);
          }}
        />
      )}
    </div>
  );
};

// Modal Component
const MaintenanceModal = ({ record, trucks, year, onClose, onSave }) => {
  const isProgrammed = record?.isProgrammed ?? (record?.km_actual < record?.km_mantenimiento);
  
  // Obtener fecha actual en formato YYYY-MM-DD
  const today = new Date().toISOString().split('T')[0];
  
  // Generar años disponibles desde 2023 hasta el año actual + 1
  const currentYear = new Date().getFullYear();
  const availableYears = Array.from(
    { length: currentYear - 2023 + 2 }, 
    (_, i) => 2023 + i
  );
  
  const [formData, setFormData] = useState({
    truck_id: record?.truck_id || '',
    tipo_mantenimiento: record?.tipo_mantenimiento || 'Preventivo',
    fecha_mantenimiento: record?.fecha_mantenimiento?.split('T')[0] || today,
    km_mantenimiento: record?.km_mantenimiento || (record?.km_actual ? record.km_actual + 10000 : 10000),
    km_actual: record?.km_actual || 0,
    prox_cambio: record?.prox_cambio || (record?.km_actual ? record.km_actual + 10000 : 10000),
    year: record?.year || year,
    meses: record?.meses || {},
    observaciones: record?.observaciones || ''
  });

  const monthsToShow = year === 2023 
    ? ['Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic']
    : ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      // Solo es edición si el record tiene un id válido
      const isEdit = record?.id && record.id > 0;
      
      console.log('Guardando:', { isEdit, formData });
      
      let response;
      if (isEdit) {
        response = await api.put(`/maintenance-schedule/${record.id}`, formData);
      } else {
        response = await api.post('/maintenance-schedule', formData);
      }
      
      console.log('Guardado exitoso:', response.data);
      onSave();
    } catch (error) {
      console.error('Error:', error);
      alert('Error al guardar el registro');
    }
  };

  const toggleWeek = (month, week) => {
    setFormData(prev => ({
      ...prev,
      meses: {
        ...prev.meses,
        [month]: {
          ...(prev.meses[month] || {}),
          [week]: !prev.meses[month]?.[week]
        }
      }
    }));
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="bg-white rounded-3xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-8">
          <h2 className="text-2xl font-bold text-gray-800 mb-2 flex items-center gap-3">
            <Wrench className="w-7 h-7 text-blue-600" />
            {record?.id ? 'Editar Registro' : 'Nuevo Registro de Mantenimiento'}
          </h2>
          
          {/* Indicador de tipo */}
          {record && (
            <div className={`mb-4 p-3 rounded-lg flex items-center gap-2 ${
              isProgrammed 
                ? 'bg-purple-50 border border-purple-200' 
                : 'bg-green-50 border border-green-200'
            }`}>
              {isProgrammed ? (
                <>
                  <Calendar className="w-5 h-5 text-purple-600" />
                  <span className="text-sm font-medium text-purple-900">
                    Mantenimiento PROGRAMADO (Preventivo futuro)
                  </span>
                </>
              ) : (
                <>
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  <span className="text-sm font-medium text-green-900">
                    Mantenimiento REALIZADO (Preventivo cumplido o Correctivo)
                  </span>
                </>
              )}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Unidad *
                </label>
                <select
                  required
                  value={formData.truck_id}
                  onChange={(e) => {
                    const selectedTruck = trucks.find(t => t.id === parseInt(e.target.value));
                    setFormData({ 
                      ...formData, 
                      truck_id: e.target.value,
                      km_actual: selectedTruck?.km_actual || 0
                    });
                  }}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  disabled={!!record?.numero_economico}
                >
                  <option value="">Seleccionar unidad</option>
                  {trucks.map(truck => (
                    <option key={truck.id} value={truck.id}>
                      {truck.numero_economico} - {truck.tipo_unidad} (Km: {truck.km_actual?.toLocaleString()})
                    </option>
                  ))}
                </select>
                {record?.numero_economico && (
                  <p className="mt-1 text-xs text-gray-500">
                    Unidad bloqueada: {record.numero_economico}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Tipo de Mantenimiento *
                </label>
                <select
                  required
                  value={formData.tipo_mantenimiento}
                  onChange={(e) => setFormData({ ...formData, tipo_mantenimiento: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="Preventivo">Preventivo</option>
                  <option value="Correctivo">Correctivo</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Fecha de Mantenimiento *
                </label>
                <input
                  type="date"
                  required
                  value={formData.fecha_mantenimiento}
                  onChange={(e) => setFormData({ ...formData, fecha_mantenimiento: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Año *
                </label>
                <select
                  required
                  value={formData.year}
                  onChange={(e) => setFormData({ ...formData, year: Number(e.target.value) })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  {availableYears.map(year => (
                    <option key={year} value={year}>{year}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Km al Mantenimiento
                </label>
                <input
                  type="number"
                  value={formData.km_mantenimiento}
                  onChange={(e) => setFormData({ ...formData, km_mantenimiento: Number(e.target.value) })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Km Actual
                </label>
                <input
                  type="number"
                  value={formData.km_actual}
                  onChange={(e) => setFormData({ ...formData, km_actual: Number(e.target.value) })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Próximo Cambio (Km)
                </label>
                <input
                  type="number"
                  value={formData.prox_cambio}
                  onChange={(e) => setFormData({ ...formData, prox_cambio: Number(e.target.value) })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>

            {/* Calendario Mensual */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Calendario de Mantenimientos (marcar semanas realizadas)
              </label>
              <div className="grid grid-cols-7 gap-3">
                {monthsToShow.map(month => (
                  <div key={month} className="border border-gray-200 rounded-lg p-3">
                    <div className="text-center font-semibold text-sm text-gray-700 mb-2">
                      {month}
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      {[1, 2, 3, 4].map(week => (
                        <button
                          key={week}
                          type="button"
                          onClick={() => toggleWeek(month, week)}
                          className={`p-2 rounded text-xs font-medium transition-colors ${
                            formData.meses[month]?.[week]
                              ? 'bg-green-500 text-white'
                              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                          }`}
                        >
                          S{week}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Observaciones
              </label>
              <textarea
                value={formData.observaciones}
                onChange={(e) => setFormData({ ...formData, observaciones: e.target.value })}
                rows="3"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Notas adicionales sobre el mantenimiento..."
              />
            </div>

            <div className="flex gap-4 justify-end pt-4 border-t">
              <motion.button
                type="button"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={onClose}
                className="px-6 py-3 border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 transition-colors"
              >
                Cancelar
              </motion.button>
              <motion.button
                type="submit"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl hover:from-blue-700 hover:to-indigo-700 shadow-lg transition-all"
              >
                {record ? 'Actualizar' : 'Guardar'}
              </motion.button>
            </div>
          </form>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default MaintenanceSchedulePage;
